import AddIcon from '@mui/icons-material/Add';
import { Box, Grid, List, ListItemButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IViewers, IWorkspace, getWorkspaceUsers } from 'wisestep-api';
import {
  CardVariant,
  EmptyTeamsAndUsersIcon,
  WsButton,
  WsCard,
  WsDrawer,
  WsIconMoreVer,
  WsListingLoader,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
} from 'wisestep-base';

import PersonAvatar from '../../../images/Avatar/Person.svg';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import PopoverOnClick from '../../clientListingPage/components/PopoverOnClick';
import WsDataGridPagination from '../../submissionsNew/components/widgets/WsDataGridPagination';
import { StyledDataGrid } from '../../teams/components/TeamListNew';
import WsAvatar from '../../teams/components/WsAvatar';
import { MethodType, viewersApisCalls, RoleTypes, ScopeType } from '../helper/ClientSettingsHelper';

import AddViewers from './AddViewers';
import { StyledPaginationBox } from './OrganizationalUnitLayoutNew';

interface IViewersList {
  organizationalUnitId: string;
  readonly: boolean;
  organizationalUnitName: string;
  scopeType: string;
}

const ViwersList = ({
  organizationalUnitId,
  readonly,
  organizationalUnitName,
  scopeType,
}: IViewersList) => {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [addViewerOpen, setAddViewerOpen] = useState(false);
  const reload = () => setAddViewerOpen(false);
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [selectedViewers, setSelectedViewers] = useState<IWorkspace[]>([]);
  const [allSelectedUsers, setAllSelectedUsers] = useState<string[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<IWorkspace[]>([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await viewersApisCalls(scopeType, MethodType.Get, organizationalUnitId);
      setAllSelectedUsers(response?.data.map((item) => item.accountManagerId));
      const userIds =
        scopeType === ScopeType.Account_Managers
          ? response?.data.map((item) => item.type === 'Viewers' && item.accountManagerId)
          : response?.data[0]?.viewersIds || [];
      if (userIds && !isEmpty(userIds)) {
        const query = {
          roles:
            scopeType === ScopeType.Account_Managers
              ? [
                  RoleTypes.Analyst,
                  RoleTypes.Head,
                  RoleTypes.Lead,
                  RoleTypes.User,
                  RoleTypes.ACCOUNT_MANAGER,
                ]
              : [],
          statuses: ['all'],
          userIds: userIds,
          page: 0,
          size: 50,
        };
        const req = await getWorkspaceUsers(query);
        const selectedData = req.data.workspaceUsers;
        setSelectedViewers(selectedData);
      } else {
        setSelectedViewers([]);
      }
    } catch (error) {
      setSelectedViewers([]);
    } finally {
      setLoading(false);
    }
  }, [organizationalUnitId, scopeType]);

  useEffect(() => {
    const getInvitedUsers = async () => {
      if (allSelectedUsers && allSelectedUsers.length > 0) {
        const query = {
          roles: [
            RoleTypes.Analyst,
            RoleTypes.Head,
            RoleTypes.Lead,
            RoleTypes.User,
            RoleTypes.ACCOUNT_MANAGER,
          ],
          statuses: ['all'],
          userIds: allSelectedUsers,
          page: 0,
          size: 50,
        };
        const req = await getWorkspaceUsers(query);
        const selectedData = req.data.workspaceUsers;
        setSelectedMembers(selectedData);
      } else {
        setSelectedMembers([]);
      }
    };
    getInvitedUsers();
  }, [allSelectedUsers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const data = useMemo(
    () =>
      selectedViewers?.map((data) => ({
        id: data?.userId,
        organizationalUnitId: organizationalUnitId,
        name: data?.userName ?? 'NA',
        email: data?.userId ?? 'NA',
      })),
    [organizationalUnitId, selectedViewers]
  );

  const columns: GridColDef[] = [
    {
      headerName: 'User Name',
      field: 'name',
      flex: 1,
      renderCell: (param) => (
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <WsAvatar src={PersonAvatar} size="small" variant="circular" />
          </Grid>
          <Grid item>
            <WsTypography>{param.value}</WsTypography>
          </Grid>
        </Grid>
      ),
    },
    {
      headerName: 'Email',
      field: 'email',
      flex: 1,
      renderCell: (param) => <WsTypography>{param.value}</WsTypography>,
    },
  ];

  if (!readonly) {
    columns.push({
      headerName: '',
      field: 'actions',
      width: 50,
      renderCell: (param) => (
        <PopoverOnClick element={<WsIconMoreVer />}>
          <List sx={{ borderRadius: 5, width: 150 }}>
            <ListItemButton onClick={() => handleDeleteViewers(param.row)}>
              <WsTypography
                variant={WsTypoVariants.SUB_TITLE}
                cursor="pointer"
                color={WsTypoColors.RED}
              >
                Remove Item
              </WsTypography>
            </ListItemButton>
          </List>
        </PopoverOnClick>
      ),
    });
  }

  const addViewers = async (data) => {
    try {
      if (organizationalUnitId) {
        const payload: IViewers = {
          organizationalId: organizationalUnitId,
          viewersIds: data?.viewerList,
        };
        const res = await viewersApisCalls(scopeType, MethodType.Add, '', payload);
        if (res?.status === 200) {
          setAddViewerOpen(false);
          setNotification(success(`Viewers has been added successfully !!`));
          fetchData();
        } else {
          setNotification(warning('Something went wrong!!'));
        }
      }
    } catch (error) {
      setNotification(warning('Something went wrong!!'));
    }
  };

  const handleDrawerOpen = () => setAddViewerOpen(true);

  const handleDeleteViewers = async (rowObj: any) => {
    try {
      const payload: IViewers = {
        organizationalId: organizationalUnitId,
        viewersIds: [rowObj.email],
      };
      const res = await viewersApisCalls(scopeType, MethodType.Delete, '', payload);
      if (res && res.status === 200) {
        setNotification(
          success(`${rowObj.name} is removed from ${organizationalUnitName} successfully!!`)
        );
        fetchData();
      } else {
        setNotification(warning('Something went wrong!!'));
      }
    } catch (error) {
      setNotification(warning('Something went wrong!!'));
    }
  };

  const paginationComponent = (
    <StyledPaginationBox>
      <Box ml={2} display="flex" gap={1}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>Total Count:</WsTypography>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
          {data.length}
        </WsTypography>
      </Box>
      {data.length > 10 && (
        <WsDataGridPagination
          count={Math.ceil(data.length / pageSize)}
          page={pageNo + 1}
          onPageChange={(_$, newPage) => setPageNo(newPage - 1)}
          pageSize={pageSize}
          onPageSizeChange={({ target: { value } }) => setPageSize(value)}
        />
      )}
    </StyledPaginationBox>
  );

  return (
    <Grid container mt={2} justifyContent={'space-between'}>
      <Grid container item xs={12}>
        {!readonly && (
          <Box marginLeft="auto" pb={2}>
            <WsButton startIcon={<AddIcon />} onClick={handleDrawerOpen}>{`Add Viewers`}</WsButton>
          </Box>
        )}
        <Grid item xs={12}>
          {isLoading ? (
            <WsListingLoader />
          ) : (
            <>
              {isEmpty(selectedViewers) ? (
                <WsCard
                  title={''}
                  sx={{ mt: 4 }}
                  isButton={false}
                  cardType={CardVariant.WITH_LIGHT_BG}
                  middleComponent={
                    <Grid
                      container
                      direction="column"
                      alignItems="center"
                      justifyContent="center"
                      spacing={3}
                    >
                      <EmptyTeamsAndUsersIcon />
                      <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                        Its lonely here
                      </WsTypography>
                      <Grid item>
                        <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                          Add Viewers from your workspace.
                        </WsTypography>
                      </Grid>
                    </Grid>
                  }
                />
              ) : (
                <>
                  {paginationComponent}
                  <StyledDataGrid
                    autoHeight
                    hideFooter
                    pageSize={pageSize}
                    page={pageNo}
                    columns={columns}
                    rows={data ?? []}
                  />
                  {data.length > 10 && paginationComponent}
                </>
              )}
            </>
          )}
        </Grid>
      </Grid>
      <WsDrawer
        open={addViewerOpen}
        onClose={reload}
        title={'Select Viewers'}
        headerSize="medium"
        size="small"
      >
        <AddViewers
          selectedViewers={selectedMembers}
          onInput={(data) => addViewers(data)}
          scopeType={scopeType}
        />
      </WsDrawer>
    </Grid>
  );
};

export default ViwersList;
